if (!customElements.get("push-grid-layout")) {
  class PushGridLayout extends HTMLElement {
    constructor() {
      super();
      this.targetSelector = this.getAttribute("target") || "#product-grid";
      this.signature = this.dataset.pushSignature || "";
      this.productsPerPage = parseInt(this.dataset.productsPerPage || "0", 10) || 0;
      this.pageStart = parseInt(this.dataset.pageStart || "1", 10) || 1;
      this.gridObserver = null;
      this.rafId = null;
      this.lastWidth = 0;
      this.boundResize = () => {
        const currentWidth = window.innerWidth;
        if (currentWidth !== this.lastWidth) {
          this.lastWidth = currentWidth;
          this.scheduleLayout();
        }
      };
      this.boundWindowLoad = () => this.scheduleLayout();
    }

    connectedCallback() {
      this.lastWidth = window.innerWidth;
      this.invalidateInfiniteScrollCache();
      this.connectToGrid();
      window.addEventListener("resize", this.boundResize);
      window.addEventListener("load", this.boundWindowLoad);
      this.scheduleLayout();
    }

    disconnectedCallback() {
      this.disconnectObserver();
      window.removeEventListener("resize", this.boundResize);
      window.removeEventListener("load", this.boundWindowLoad);

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    connectToGrid() {
      this.grid = document.querySelector(this.targetSelector);
      this.disconnectObserver();

      if (!this.grid) return;

      this.gridObserver = new MutationObserver(() => this.scheduleLayout());
      this.gridObserver.observe(this.grid, { childList: true, subtree: false });
    }

    disconnectObserver() {
      if (!this.gridObserver) return;
      this.gridObserver.disconnect();
      this.gridObserver = null;
    }

    scheduleLayout() {
      if (this.rafId) cancelAnimationFrame(this.rafId);

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;

        if (!this.grid || !document.body.contains(this.grid)) {
          this.connectToGrid();
        }

        if (!this.grid) return;
        this.layoutPushes();
      });
    }

    invalidateInfiniteScrollCache() {
      if (!this.signature) return;

      const key = "PushGridLayout_Signature";

      try {
        const previous = sessionStorage.getItem(key);

        if (previous && previous !== this.signature) {
          Object.keys(sessionStorage).forEach((storageKey) => {
            if (storageKey.indexOf("InfiniteScroll_") === 0) {
              sessionStorage.removeItem(storageKey);
            }
          });
        }

        sessionStorage.setItem(key, this.signature);
      } catch (e) {
        // no-op
      }
    }

    layoutPushes() {
      if (!this.grid) return;

      const alreadyLaidOut = this.grid.classList.contains("push-layout-ready");
      this.grid.classList.remove("push-layout-ready");

      const allPushes = Array.from(this.grid.querySelectorAll(".grid__item.push"));
      if (!allPushes.length) {
        this.grid.classList.add("push-layout-ready");
        return;
      }

      // Product pushes flow naturally in the DOM — no grid placement needed
      const productPushes = [];
      const collectionPushes = [];

      allPushes.forEach((push) => {
        if (push.dataset.pushType === "product") {
          productPushes.push(push);
        } else {
          collectionPushes.push(push);
        }
      });

      productPushes.forEach((push) => {
        push.style.display = "";
        push.style.visibility = "visible";
        push.style.gridColumn = "";
        push.style.gridRow = "";
      });

      const cols = window.matchMedia("(max-width: 749px)").matches ? 2 : 4;
      const productItems = Array.from(this.grid.querySelectorAll(".grid__item:not(.push)"));
      const productCount = productItems.length;

      const infiniteScrollEnabled = !!document.querySelector("infinite-scroll");
      const hasAppendedPages =
        infiniteScrollEnabled &&
        this.productsPerPage > 0 &&
        productCount > this.productsPerPage;

      const seenKeys = new Set();
      const uniquePushes = [];

      collectionPushes.forEach((push, index) => {
        const key = push.dataset.pushKey || `push-${index}`;

        if (seenKeys.has(key)) {
          push.style.display = "none";
          push.style.visibility = "hidden";
          return;
        }

        seenKeys.add(key);
        uniquePushes.push(push);
      });

      const pushData = uniquePushes
        .map((push, index) => {
          const rawWidth = parseInt(push.dataset.pushWidth || "1", 10);
          const rawHeight = parseInt(push.dataset.pushHeight || "1", 10);
          const rawPos = parseInt(push.dataset.pushPos || "0", 10);

          const w = Math.max(1, Math.min(cols, Number.isNaN(rawWidth) ? 1 : rawWidth));
          const h = Math.max(1, Number.isNaN(rawHeight) ? 1 : rawHeight);
          const pos = Number.isNaN(rawPos) ? 0 : rawPos;

          return { push, index, w, h, pos };
        })
        .sort((a, b) => (a.pos === b.pos ? a.index - b.index : a.pos - b.pos));

      const maxSearch = Math.max(500, productCount + pushData.length * cols * 8);

      const canPlaceInGrid = (occupiedSet, candidate, w, h) => {
        const col = ((candidate - 1) % cols) + 1;
        if (col + w - 1 > cols) return null;

        let occupiedBefore = 0;
        occupiedSet.forEach((cell) => {
          if (cell < candidate) occupiedBefore += 1;
        });

        const requiredProductsBefore = candidate - 1 - occupiedBefore;
        if (requiredProductsBefore > productCount) return null;

        const row = Math.floor((candidate - 1) / cols) + 1;

        for (let r = 0; r < h; r += 1) {
          for (let c = 0; c < w; c += 1) {
            const cell = (row + r - 1) * cols + (col + c);
            if (occupiedSet.has(cell)) return null;
          }
        }

        return { row, col };
      };

      const reserveCells = (occupiedSet, row, col, w, h) => {
        for (let r = 0; r < h; r += 1) {
          for (let c = 0; c < w; c += 1) {
            const cell = (row + r - 1) * cols + (col + c);
            occupiedSet.add(cell);
          }
        }
      };

      const activePushes = [];

      if (hasAppendedPages) {
        let filledCells = productCount;

        pushData.forEach((entry) => {
          const desiredPos = Math.max(1, entry.pos || 1);
          const canReveal = desiredPos <= filledCells;

          if (canReveal) {
            activePushes.push(entry);
            filledCells += entry.w * entry.h;
          } else {
            entry.push.style.display = "none";
            entry.push.style.visibility = "hidden";
          }
        });
      } else {
        pushData.forEach((entry) => {
          if (entry.push.dataset.initialVisible === "true") {
            activePushes.push(entry);
          } else {
            entry.push.style.display = "none";
            entry.push.style.visibility = "hidden";
          }
        });
      }

      if (!activePushes.length) {
        this.grid.classList.add("push-layout-ready");
        return;
      }

      activePushes.forEach(({ push }) => {
        push.style.display = "";
        if (!alreadyLaidOut) push.style.visibility = "hidden";
        push.style.gridColumn = "";
        push.style.gridRow = "";
      });

      const occupied = new Set();

      activePushes.forEach(({ push, w, h, pos }) => {
        const desired = hasAppendedPages
          ? Math.max(1, pos || 1)
          : Math.max(1, (pos || 1) - this.pageStart + 1);

        let row = 1;
        let col = 1;
        let placed = false;

        for (let candidate = desired; candidate <= maxSearch; candidate += 1) {
          const result = canPlaceInGrid(occupied, candidate, w, h);
          if (!result) continue;

          row = result.row;
          col = result.col;
          placed = true;
          break;
        }

        if (!placed) {
          for (let candidate = desired - 1; candidate >= 1; candidate -= 1) {
            const result = canPlaceInGrid(occupied, candidate, w, h);
            if (!result) continue;

            row = result.row;
            col = result.col;
            placed = true;
            break;
          }
        }

        push.style.gridColumn = `${col} / span ${w}`;
        push.style.gridRow = `${row} / span ${h}`;

        reserveCells(occupied, row, col, w, h);
      });

      requestAnimationFrame(() => {
        activePushes.forEach(({ push }) => {
          push.style.visibility = "visible";
        });

        this.grid.classList.add("push-layout-ready");
      });
    }
  }

  customElements.define("push-grid-layout", PushGridLayout);
}